import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import Modal from './ui/Modal';
import ConfirmDialog from './ui/ConfirmDialog';
import { AdminQueueState } from './ui/AdminQueueState';
import { useToast } from '../contexts/ToastContext';
import {
  adminGetAchievementDefinitions,
  adminGetIssuedAchievements,
  adminSeedDefaultAchievements,
  adminSaveAchievementDefinition,
  adminSetAchievementDefinitionActive,
  adminRecalculateAchievementDefinition,
  adminRecalculateVehicleAchievements,
  adminRevokeAchievement,
  adminRestoreAchievement,
  adminPreviewAchievementDefinition,
} from '../services/api/adminExpansion';
import type {
  AdminAchievementDefinition,
  AdminIssuedAchievement,
  AchievementCalculationType,
  AchievementDefinitionInput,
} from '../services/api/adminExpansion';

const prettyLabel = (value: string): string =>
  value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const classLabel = (achievementClass: string): string =>
  achievementClass === 'platform_achievement' ? 'Platform achievement' : 'Build milestone';

const classBadgeClass = (achievementClass: string): string =>
  achievementClass === 'platform_achievement'
    ? 'bg-orange-500/10 text-orange-300 border border-orange-500/30'
    : 'bg-sky-950/50 text-sky-300 border border-sky-800';

const criteriaLabel = (def: AdminAchievementDefinition): string => {
  if (def.calculation_type === 'threshold') {
    return def.threshold !== null ? `At least ${def.threshold}` : 'Threshold not set';
  }
  if (def.calculation_type === 'rank') {
    return def.rank_limit !== null ? `Top ${def.rank_limit}` : 'Rank limit not set';
  }
  return def.percentile_limit !== null ? `Top ${def.percentile_limit}%` : 'Percentile not set';
};

const formatDate = (value: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

interface DefinitionForm {
  slug: string;
  name: string;
  short_name: string;
  description: string;
  achievement_class: string;
  category: string;
  calculation_type: AchievementCalculationType;
  metric: string;
  scope_type: string;
  threshold: string;
  rank_limit: string;
  percentile_limit: string;
  minimum_sample_size: string;
  visual_tier: string;
  is_public: boolean;
}

const emptyForm: DefinitionForm = {
  slug: '',
  name: '',
  short_name: '',
  description: '',
  achievement_class: 'build_milestone',
  category: 'documentation',
  calculation_type: 'threshold',
  metric: '',
  scope_type: 'overall',
  threshold: '',
  rank_limit: '',
  percentile_limit: '',
  minimum_sample_size: '0',
  visual_tier: 'standard',
  is_public: true,
};

const fieldClass =
  'input';

export const AdminAchievements: React.FC = () => {
  const { showToast } = useToast();

  const [definitions, setDefinitions] = useState<AdminAchievementDefinition[]>([]);
  const [metrics, setMetrics] = useState<string[]>([]);
  const [scopeTypes, setScopeTypes] = useState<string[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [previewCounts, setPreviewCounts] = useState<Record<number, number>>({});
  const [seeding, setSeeding] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [form, setForm] = useState<DefinitionForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [savingDefinition, setSavingDefinition] = useState(false);

  const [issuedFor, setIssuedFor] = useState<AdminAchievementDefinition | null>(null);
  const [issued, setIssued] = useState<AdminIssuedAchievement[]>([]);
  const [issuedLoading, setIssuedLoading] = useState(false);
  const [issuedError, setIssuedError] = useState<string | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<AdminIssuedAchievement | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const [vehicleIdInput, setVehicleIdInput] = useState('');
  const [recalcVehicleBusy, setRecalcVehicleBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGetAchievementDefinitions();
      setDefinitions(data.definitions);
      setMetrics(data.metrics);
      setScopeTypes(data.scope_types);
      setClasses(data.classes);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load achievement definitions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const staleDefinitions = definitions.filter((d) => d.stale);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const inserted = await adminSeedDefaultAchievements();
      showToast(
        inserted > 0
          ? `Seeded ${inserted} default milestone definition${inserted === 1 ? '' : 's'}.`
          : 'Default milestones already exist. Nothing was added.',
        'success'
      );
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to seed defaults', 'error');
    } finally {
      setSeeding(false);
    }
  };

  const openCreate = () => {
    setEditingSlug(null);
    setForm({ ...emptyForm, metric: metrics[0] ?? '' });
    setFormError(null);
    setEditorOpen(true);
  };

  const openEdit = (def: AdminAchievementDefinition) => {
    setEditingSlug(def.slug);
    setForm({
      slug: def.slug,
      name: def.name,
      short_name: def.short_name ?? '',
      description: def.description ?? '',
      achievement_class: def.achievement_class,
      category: def.category,
      calculation_type: def.calculation_type,
      metric: def.metric,
      scope_type: def.scope_type,
      threshold: def.threshold !== null ? String(def.threshold) : '',
      rank_limit: def.rank_limit !== null ? String(def.rank_limit) : '',
      percentile_limit: def.percentile_limit !== null ? String(def.percentile_limit) : '',
      minimum_sample_size: String(def.minimum_sample_size),
      visual_tier: def.visual_tier,
      is_public: def.is_public,
    });
    setFormError(null);
    setEditorOpen(true);
  };

  const setField = <K extends keyof DefinitionForm>(key: K, value: DefinitionForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveDefinition = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const slug = form.slug.trim().toLowerCase();
    if (!/^[a-z0-9-]{2,80}$/.test(slug)) {
      setFormError('Slug must be 2 to 80 lowercase letters, numbers, or hyphens.');
      return;
    }
    if (!form.name.trim() || form.name.trim().length > 160) {
      setFormError('Name is required (up to 160 characters).');
      return;
    }
    if (!/^[a-z_]{2,40}$/.test(form.category.trim())) {
      setFormError('Category must be 2 to 40 lowercase letters or underscores.');
      return;
    }
    if (!form.metric) {
      setFormError('Pick a metric.');
      return;
    }
    const payload: AchievementDefinitionInput = {
      slug,
      name: form.name.trim(),
      short_name: form.short_name.trim() || undefined,
      description: form.description.trim() || undefined,
      achievement_class: form.achievement_class,
      category: form.category.trim(),
      calculation_type: form.calculation_type,
      metric: form.metric,
      scope_type: form.scope_type,
      minimum_sample_size: Number(form.minimum_sample_size) || 0,
      visual_tier: form.visual_tier,
      is_public: form.is_public,
    };
    if (form.calculation_type === 'threshold') {
      const threshold = Number(form.threshold);
      if (form.threshold === '' || !Number.isFinite(threshold)) {
        setFormError('Threshold definitions need a numeric threshold.');
        return;
      }
      payload.threshold = threshold;
    } else if (form.calculation_type === 'rank') {
      const rankLimit = Number(form.rank_limit);
      if (form.rank_limit === '' || !Number.isInteger(rankLimit) || rankLimit < 1) {
        setFormError('Rank definitions need a whole number rank limit of 1 or more.');
        return;
      }
      payload.rank_limit = rankLimit;
    } else {
      const pct = Number(form.percentile_limit);
      if (form.percentile_limit === '' || !Number.isFinite(pct) || pct < 0.1 || pct > 100) {
        setFormError('Percentile definitions need a percentile between 0.1 and 100.');
        return;
      }
      payload.percentile_limit = pct;
    }

    setSavingDefinition(true);
    try {
      const result = await adminSaveAchievementDefinition(payload);
      showToast(
        result.created ? 'Definition created.' : 'Definition updated. Version was bumped.',
        'success'
      );
      setEditorOpen(false);
      await load();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to save definition');
    } finally {
      setSavingDefinition(false);
    }
  };

  const toggleActive = async (def: AdminAchievementDefinition) => {
    const key = `active-${def.id}`;
    setBusyKey(key);
    try {
      await adminSetAchievementDefinitionActive(def.id, !def.is_active);
      showToast(`${def.name} is now ${def.is_active ? 'inactive' : 'active'}.`, 'success');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to update definition', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const recalculateDefinition = async (def: AdminAchievementDefinition) => {
    const key = `recalc-${def.id}`;
    setBusyKey(key);
    try {
      await adminRecalculateAchievementDefinition(def.id);
      showToast(`Recalculated ${def.name}.`, 'success');
      await load();
      if (issuedFor?.id === def.id) {
        await refreshIssued(def);
      }
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Recalculation failed', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const previewDefinition = async (def: AdminAchievementDefinition) => {
    const key = `preview-${def.id}`;
    setBusyKey(key);
    try {
      const count = await adminPreviewAchievementDefinition(def.id);
      setPreviewCounts((prev) => ({ ...prev, [def.id]: count }));
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Preview failed', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const refreshIssued = async (def: AdminAchievementDefinition) => {
    setIssuedLoading(true);
    setIssuedError(null);
    try {
      const rows = await adminGetIssuedAchievements(def.id);
      setIssued(rows);
    } catch (err: unknown) {
      setIssuedError(err instanceof Error ? err.message : 'Failed to load issued achievements');
      setIssued([]);
    } finally {
      setIssuedLoading(false);
    }
  };

  const viewIssued = async (def: AdminAchievementDefinition) => {
    setIssuedFor(def);
    await refreshIssued(def);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    if (!revokeReason.trim()) {
      setRevokeError('A reason is required to revoke an achievement.');
      return;
    }
    try {
      await adminRevokeAchievement(revokeTarget.id, revokeReason.trim());
      showToast('Achievement revoked.', 'success');
      setRevokeTarget(null);
      setRevokeReason('');
      setRevokeError(null);
      if (issuedFor) await refreshIssued(issuedFor);
      await load();
    } catch (err: unknown) {
      setRevokeError(err instanceof Error ? err.message : 'Failed to revoke achievement');
    }
  };

  const restoreAchievement = async (row: AdminIssuedAchievement) => {
    const key = `restore-${row.id}`;
    setBusyKey(key);
    try {
      await adminRestoreAchievement(row.id);
      showToast('Achievement restored.', 'success');
      if (issuedFor) await refreshIssued(issuedFor);
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to restore achievement', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const recalculateVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    const vehicleId = Number(vehicleIdInput);
    if (!Number.isInteger(vehicleId) || vehicleId <= 0) {
      showToast('Enter a valid vehicle ID.', 'error');
      return;
    }
    setRecalcVehicleBusy(true);
    try {
      const result = await adminRecalculateVehicleAchievements(vehicleId);
      const scorePart =
        result.documentation_score !== null
          ? ` Documentation score is now ${result.documentation_score}.`
          : '';
      showToast(
        `Vehicle ${vehicleId} recalculated. ${result.issued} new milestone${
          result.issued === 1 ? '' : 's'
        } issued.${scorePart}`,
        'success'
      );
      setVehicleIdInput('');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Vehicle recalculation failed', 'error');
    } finally {
      setRecalcVehicleBusy(false);
    }
  };

  const isComparative = (def: AdminAchievementDefinition) =>
    def.calculation_type === 'rank' || def.calculation_type === 'percentile';

  return (
    <div className="space-y-8 max-w-6xl">
      <PageHeader
        eyebrow="Admin"
        title="Achievements"
        description="Manage achievement definitions, recalculate comparative awards, and review issued achievements."
        actions={
          <>
            <Button type="button" variant="secondary" loading={seeding} onClick={() => void seedDefaults()}>
              Seed default milestones
            </Button>
            <Button type="button" variant="primary" onClick={openCreate}>
              New definition
            </Button>
          </>
        }
      />

      {error ? (
        <AdminQueueState
          variant="error"
          title="Could not load achievement definitions"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}

      {staleDefinitions.length > 0 ? (
        <div
          role="status"
          className="rounded-lg border border-amber-800 bg-amber-950/30 p-4 text-amber-200 text-sm space-y-1"
        >
          <p className="font-medium">
            {staleDefinitions.length} comparative definition{staleDefinitions.length === 1 ? '' : 's'} may be
            stale (last calculated more than 7 days ago):
          </p>
          <ul className="list-disc list-inside">
            {staleDefinitions.map((d) => (
              <li key={d.id}>
                {d.name} (last calculated {formatDate(d.last_calculated_at)})
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <form
        onSubmit={recalculateVehicle}
        className="form-section flex flex-col sm:flex-row sm:items-end gap-3"
      >
        <div className="flex-1">
          <label htmlFor="achievement-recalc-vehicle" className="input-label">
            Recalculate milestones for one vehicle
          </label>
          <input
            id="achievement-recalc-vehicle"
            type="number"
            min={1}
            inputMode="numeric"
            value={vehicleIdInput}
            onChange={(e) => setVehicleIdInput(e.target.value)}
            placeholder="Vehicle ID"
            className={fieldClass}
          />
        </div>
        <Button type="submit" variant="secondary" loading={recalcVehicleBusy} loadingLabel="Recalculating…">
          Recalculate vehicle
        </Button>
      </form>

      <section aria-label="Achievement definitions">
        <h2 className="text-white font-medium mb-3">Definitions</h2>
        {loading ? (
          <p className="text-gray-400 text-sm" role="status">
            Loading definitions…
          </p>
        ) : definitions.length === 0 ? (
          <AdminQueueState
            variant="empty"
            title="No achievement definitions yet"
            description="Seed default milestones or create a new definition to get started."
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
            <table className="min-w-full text-sm">
              <caption className="sr-only">
                Achievement definitions with criteria, status, and issuance counts
              </caption>
              <thead className="bg-[#141414]/80 text-gray-400 text-left">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">Definition</th>
                  <th scope="col" className="px-4 py-3 font-medium">Class</th>
                  <th scope="col" className="px-4 py-3 font-medium">Category</th>
                  <th scope="col" className="px-4 py-3 font-medium">Calculation</th>
                  <th scope="col" className="px-4 py-3 font-medium">Metric</th>
                  <th scope="col" className="px-4 py-3 font-medium">Scope</th>
                  <th scope="col" className="px-4 py-3 font-medium">Criteria</th>
                  <th scope="col" className="px-4 py-3 font-medium">Version</th>
                  <th scope="col" className="px-4 py-3 font-medium">Issued</th>
                  <th scope="col" className="px-4 py-3 font-medium">Status</th>
                  <th scope="col" className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {definitions.map((def) => {
                  const preview = previewCounts[def.id];
                  return (
                    <tr key={def.id}>
                      <td className="px-4 py-3">
                        <p className="text-white">{def.name}</p>
                        <p className="text-gray-500 text-xs">{def.slug}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs whitespace-nowrap ${classBadgeClass(def.achievement_class)}`}
                        >
                          {classLabel(def.achievement_class)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{prettyLabel(def.category)}</td>
                      <td className="px-4 py-3 text-gray-300">
                        {prettyLabel(def.calculation_type)}
                        {def.stale ? (
                          <span className="block text-amber-300 text-xs mt-0.5">Stale</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{prettyLabel(def.metric)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{prettyLabel(def.scope_type)}</td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                        {criteriaLabel(def)}
                        {def.minimum_sample_size > 0 ? (
                          <span className="block text-gray-500 text-xs">
                            Min sample {def.minimum_sample_size}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-400 tabular-nums">v{def.version}</td>
                      <td className="px-4 py-3 text-gray-400 tabular-nums whitespace-nowrap">
                        {def.current_count} current / {def.total_count} total
                        {preview !== undefined ? (
                          <span className="block text-orange-300 text-xs">Eligible now: {preview}</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className={def.is_active ? 'text-green-300' : 'text-gray-500'}>
                          {def.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {!def.is_public ? (
                          <span className="block text-gray-500 text-xs">Hidden from public</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(def)}>
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            loading={busyKey === `active-${def.id}`}
                            onClick={() => void toggleActive(def)}
                          >
                            {def.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          {isComparative(def) ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              loading={busyKey === `recalc-${def.id}`}
                              onClick={() => void recalculateDefinition(def)}
                            >
                              Recalculate
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            loading={busyKey === `preview-${def.id}`}
                            onClick={() => void previewDefinition(def)}
                          >
                            Affected count
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => void viewIssued(def)}>
                            View issued
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {issuedFor ? (
        <section aria-label={`Issued achievements for ${issuedFor.name}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-white font-medium">Issued: {issuedFor.name}</h2>
            <Button type="button" variant="ghost" size="sm" onClick={() => setIssuedFor(null)}>
              Close issued view
            </Button>
          </div>
          {issuedError ? (
            <div role="alert" className="rounded-lg border border-red-800 bg-red-950/30 p-4 text-red-300 text-sm mb-3">
              {issuedError}
            </div>
          ) : null}
          {issuedLoading ? (
            <p className="text-gray-400 text-sm" role="status">
              Loading issued achievements…
            </p>
          ) : issued.length === 0 && !issuedError ? (
            <AdminQueueState
              variant="empty"
              title="No issued achievements for this definition"
              description="Achievements appear here after recalculation or manual issuance."
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
              <table className="min-w-full text-sm">
                <caption className="sr-only">Issued achievements for {issuedFor.name}</caption>
                <thead className="bg-[#141414]/80 text-gray-400 text-left">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">Vehicle</th>
                    <th scope="col" className="px-4 py-3 font-medium">Status</th>
                    <th scope="col" className="px-4 py-3 font-medium">Value</th>
                    <th scope="col" className="px-4 py-3 font-medium">Rank</th>
                    <th scope="col" className="px-4 py-3 font-medium">Percentile</th>
                    <th scope="col" className="px-4 py-3 font-medium">Issued</th>
                    <th scope="col" className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {issued.map((row) => {
                    const revoked = Boolean(row.revoked_at);
                    return (
                      <tr key={row.id}>
                        <td className="px-4 py-3">
                          <p className="text-white">{row.vehicle.label || `Vehicle #${row.vehicle.id}`}</p>
                          {row.vehicle.owner_username ? (
                            <p className="text-gray-500 text-xs">@{row.vehicle.owner_username}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <span className={revoked ? 'text-red-300' : 'text-gray-300'}>
                            {revoked ? 'Revoked' : prettyLabel(row.status ?? 'current')}
                          </span>
                          {revoked && row.revocation_reason ? (
                            <p className="text-gray-500 text-xs mt-0.5">{row.revocation_reason}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-300 tabular-nums">
                          {row.value !== null ? row.value : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                          {row.rank !== null
                            ? `#${row.rank}${row.population !== null ? ` of ${row.population}` : ''}`
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-300 tabular-nums">
                          {row.percentile !== null ? `${row.percentile}%` : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                          {formatDate(row.issued_at)}
                        </td>
                        <td className="px-4 py-3">
                          {revoked ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              loading={busyKey === `restore-${row.id}`}
                              onClick={() => void restoreAchievement(row)}
                            >
                              Restore
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setRevokeTarget(row);
                                setRevokeReason('');
                                setRevokeError(null);
                              }}
                            >
                              Revoke
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      <Modal
        isOpen={editorOpen}
        onClose={() => {
          if (!savingDefinition) setEditorOpen(false);
        }}
        title={editingSlug ? `Edit definition: ${editingSlug}` : 'New achievement definition'}
        description={
          editingSlug
            ? 'Saving criteria changes bumps the definition version. Existing snapshots keep the version they were issued under.'
            : 'Criteria are constrained to supported metrics and calculation types.'
        }
        size="lg"
      >
        <form onSubmit={saveDefinition} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="def-slug" className="input-label">
                Slug
              </label>
              <input
                id="def-slug"
                type="text"
                value={form.slug}
                onChange={(e) => setField('slug', e.target.value)}
                readOnly={Boolean(editingSlug)}
                aria-describedby="def-slug-hint"
                className={`${fieldClass} ${editingSlug ? 'opacity-60' : ''}`}
              />
              <p id="def-slug-hint" className="text-gray-500 text-xs mt-1">
                Lowercase letters, numbers, and hyphens. The slug identifies the definition.
              </p>
            </div>
            <div>
              <label htmlFor="def-name" className="input-label">
                Name
              </label>
              <input
                id="def-name"
                type="text"
                maxLength={160}
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="def-short-name" className="input-label">
                Short name (optional)
              </label>
              <input
                id="def-short-name"
                type="text"
                maxLength={60}
                value={form.short_name}
                onChange={(e) => setField('short_name', e.target.value)}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="def-category" className="input-label">
                Category
              </label>
              <input
                id="def-category"
                type="text"
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
                aria-describedby="def-category-hint"
                className={fieldClass}
              />
              <p id="def-category-hint" className="text-gray-500 text-xs mt-1">
                Lowercase letters and underscores, for example documentation or performance.
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="def-description" className="input-label">
              Description (optional)
            </label>
            <textarea
              id="def-description"
              rows={2}
              maxLength={500}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              className="input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor="def-class" className="input-label">
                Class
              </label>
              <select
                id="def-class"
                value={form.achievement_class}
                onChange={(e) => setField('achievement_class', e.target.value)}
                className={fieldClass}
              >
                {(classes.length > 0 ? classes : ['platform_achievement', 'build_milestone']).map((c) => (
                  <option key={c} value={c}>
                    {classLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="def-calc" className="input-label">
                Calculation type
              </label>
              <select
                id="def-calc"
                value={form.calculation_type}
                onChange={(e) => setField('calculation_type', e.target.value as AchievementCalculationType)}
                className={fieldClass}
              >
                <option value="threshold">Threshold</option>
                <option value="rank">Rank</option>
                <option value="percentile">Percentile</option>
              </select>
            </div>
            <div>
              <label htmlFor="def-metric" className="input-label">
                Metric
              </label>
              <select
                id="def-metric"
                value={form.metric}
                onChange={(e) => setField('metric', e.target.value)}
                className={fieldClass}
              >
                <option value="">Select metric</option>
                {metrics.map((m) => (
                  <option key={m} value={m}>
                    {prettyLabel(m)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="def-scope" className="input-label">
                Scope
              </label>
              <select
                id="def-scope"
                value={form.scope_type}
                onChange={(e) => setField('scope_type', e.target.value)}
                className={fieldClass}
              >
                {(scopeTypes.length > 0 ? scopeTypes : ['overall']).map((s) => (
                  <option key={s} value={s}>
                    {prettyLabel(s)}
                  </option>
                ))}
              </select>
            </div>
            {form.calculation_type === 'threshold' ? (
              <div>
                <label htmlFor="def-threshold" className="input-label">
                  Threshold
                </label>
                <input
                  id="def-threshold"
                  type="number"
                  step="any"
                  value={form.threshold}
                  onChange={(e) => setField('threshold', e.target.value)}
                  className={fieldClass}
                />
              </div>
            ) : null}
            {form.calculation_type === 'rank' ? (
              <div>
                <label htmlFor="def-rank-limit" className="input-label">
                  Rank limit
                </label>
                <input
                  id="def-rank-limit"
                  type="number"
                  min={1}
                  step={1}
                  value={form.rank_limit}
                  onChange={(e) => setField('rank_limit', e.target.value)}
                  className={fieldClass}
                />
              </div>
            ) : null}
            {form.calculation_type === 'percentile' ? (
              <div>
                <label htmlFor="def-percentile" className="input-label">
                  Percentile limit
                </label>
                <input
                  id="def-percentile"
                  type="number"
                  min={0.1}
                  max={100}
                  step="any"
                  value={form.percentile_limit}
                  onChange={(e) => setField('percentile_limit', e.target.value)}
                  className={fieldClass}
                />
              </div>
            ) : null}
            <div>
              <label htmlFor="def-min-sample" className="input-label">
                Minimum sample size
              </label>
              <input
                id="def-min-sample"
                type="number"
                min={0}
                step={1}
                value={form.minimum_sample_size}
                onChange={(e) => setField('minimum_sample_size', e.target.value)}
                aria-describedby="def-min-sample-hint"
                className={fieldClass}
              />
              <p id="def-min-sample-hint" className="text-gray-500 text-xs mt-1">
                Comparative definitions enforce a floor of 5.
              </p>
            </div>
            <div>
              <label htmlFor="def-tier" className="input-label">
                Visual tier
              </label>
              <select
                id="def-tier"
                value={form.visual_tier}
                onChange={(e) => setField('visual_tier', e.target.value)}
                className={fieldClass}
              >
                <option value="standard">Standard</option>
                <option value="notable">Notable</option>
                <option value="exceptional">Exceptional</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={form.is_public}
              onChange={(e) => setField('is_public', e.target.checked)}
              className="w-4 h-4"
            />
            Publicly visible
          </label>

          {formError ? (
            <p role="alert" className="text-red-300 text-sm">
              {formError}
            </p>
          ) : null}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={savingDefinition}
              onClick={() => setEditorOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={savingDefinition} loadingLabel="Saving…">
              {editingSlug ? 'Save changes' : 'Create definition'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(revokeTarget)}
        title="Revoke achievement"
        description={
          revokeTarget
            ? `Revoke "${revokeTarget.name ?? revokeTarget.slug}" from ${
                revokeTarget.vehicle.label || `vehicle #${revokeTarget.vehicle.id}`
              }? It can be restored later.`
            : undefined
        }
        confirmLabel="Revoke"
        destructive
        loadingLabel="Revoking…"
        onConfirm={confirmRevoke}
        onCancel={() => {
          setRevokeTarget(null);
          setRevokeReason('');
          setRevokeError(null);
        }}
      >
        <div className="space-y-2">
          <label htmlFor="revoke-reason" className="block text-sm text-gray-400">
            Reason (required)
          </label>
          <textarea
            id="revoke-reason"
            rows={2}
            value={revokeReason}
            onChange={(e) => {
              setRevokeReason(e.target.value);
              setRevokeError(null);
            }}
            className="input"
          />
          {revokeError ? (
            <p role="alert" className="text-red-300 text-sm">
              {revokeError}
            </p>
          ) : null}
        </div>
      </ConfirmDialog>
    </div>
  );
};

export default AdminAchievements;
